import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { SlidersHorizontal, X, Pencil, Save } from 'lucide-react';
import { preferencesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';

/**
 * Preference Defaults — SysAdmin management of the AppSec_PreferenceDefaults
 * tier. Sets default preference values system-wide, per database, and/or per
 * role. Users' own rows always win; these fill the gap between "user chose
 * nothing" and the hardcoded catalog default.
 *
 * Resolution (most → least specific):
 *   user rows → default(role+db) → default(role) → default(db) → default(system) → catalog
 *
 * SysAdmin only — route wrapped in SysAdminGuard, API gated by [RequireSysAdmin].
 */

const ROLES = ['SysAdmin', 'AppAdmin', 'PowerUser', 'User', 'Guest'];
const TAB_ORDER = ['General', 'Display', 'Downloads', 'Notifications', 'Security', 'Debug', 'System'];

const parseOptions = (def) => {
  if (!def?.optionsJson) return null;
  try { return JSON.parse(def.optionsJson); } catch { return null; }
};

const keyOf = (prefKey, dbId, role) => `${prefKey}|${dbId ?? ''}|${role ?? ''}`;

const SectionHeader = ({ title }) => (
  <div className="stui-detail-section-header"><span>{title}</span></div>
);

const PreferenceDefaultsPage = () => {
  const { notify } = useNotifications();

  const [catalog, setCatalog] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [scopeDbId, setScopeDbId] = useState(null);     // null = System-wide
  const [scopeRole, setScopeRole] = useState(null);     // null = All Roles
  const [isEditing, setIsEditing] = useState(false);
  const [dirty, setDirty] = useState(new Map());        // keyOf → write row
  const [saveErrors, setSaveErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isDirty = dirty.size > 0;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await preferencesApi.getDefaults();
      setCatalog(res.data.catalog || []);
      setDefaults(res.data.defaults || []);
      setDatabases(res.data.databases || []);
      setLoadError(false);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Scope helpers ────────────────────────────────────────────────────────
  const savedAt = useCallback((prefKey) =>
    defaults.find(d =>
      d.prefKey === prefKey &&
      (d.databaseInstanceId ?? null) === scopeDbId &&
      (d.role ?? null) === scopeRole) || null,
  [defaults, scopeDbId, scopeRole]);

  const stagedAt = useCallback((prefKey) =>
    dirty.get(keyOf(prefKey, scopeDbId, scopeRole)),
  [dirty, scopeDbId, scopeRole]);

  /** Value the control shows at the current scope. */
  const valueAt = useCallback((def) => {
    const staged = stagedAt(def.prefKey);
    if (staged) return staged.value;                  // null after Clear → treated as unset below
    const saved = savedAt(def.prefKey);
    if (saved) return saved.value;
    return null;                                      // no override at this scope
  }, [stagedAt, savedAt]);

  const hasOverrideAt = useCallback((def) => {
    const staged = stagedAt(def.prefKey);
    if (staged) return staged.value != null;
    return savedAt(def.prefKey) != null;
  }, [stagedAt, savedAt]);

  const stage = (def, value) => {
    const row = {
      prefKey: def.prefKey,
      databaseInstanceId: scopeDbId,
      role: scopeRole,
      value: value == null ? null : String(value),
    };
    setDirty(prev => {
      const next = new Map(prev);
      next.set(keyOf(def.prefKey, scopeDbId, scopeRole), row);
      return next;
    });
    setSaveErrors(prev => { const n = { ...prev }; delete n[def.prefKey]; return n; });
  };

  // ── Save / Cancel ────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    setSaveErrors({});
    try {
      await preferencesApi.saveDefaults([...dirty.values()]);
      setDirty(new Map());
      setIsEditing(false);
      await load();
      notify.success('Preference defaults saved');
    } catch (e) {
      const errs = e?.response?.data?.errors || [];
      const map = {};
      errs.forEach(er => { if (er.prefKey) map[er.prefKey] = er.error; });
      setSaveErrors(Object.keys(map).length ? map : { _global: 'Save failed — check connection and try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!isDirty) { setIsEditing(false); return; }
    notify.confirm('Discard unsaved default changes?', () => {
      setDirty(new Map());
      setSaveErrors({});
      setIsEditing(false);
    }, { confirmText: 'Discard', cancelText: 'Keep Editing' });
  };

  // ── Rendering ────────────────────────────────────────────────────────────
  const scopeLabel = useMemo(() => {
    const db = scopeDbId == null ? 'System-wide' : (databases.find(d => d.id === scopeDbId)?.displayName || `DB ${scopeDbId}`);
    const role = scopeRole == null ? 'All Roles' : scopeRole;
    return `${db} · ${role}`;
  }, [scopeDbId, scopeRole, databases]);

  const renderControl = (def) => {
    const isFuture = def.status === 'future';
    const disabled = isFuture || !isEditing;
    const overridden = hasOverrideAt(def);
    const value = valueAt(def) ?? def.defaultValue;   // display falls back to catalog default
    const options = parseOptions(def);
    const err = saveErrors[def.prefKey];

    let control = null;
    if (def.dataType === 'bool') {
      control = (
        <label className="stui-toggle">
          <input type="checkbox" checked={value === 'true'} disabled={disabled}
            onChange={e => stage(def, e.target.checked ? 'true' : 'false')} />
          <span className="stui-toggle-slider" />
        </label>
      );
    } else if (options && options.length > 6) {
      control = (
        <select className="stui-select" value={value || ''} disabled={disabled}
          onChange={e => stage(def, e.target.value)}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    } else if (options) {
      control = (
        <div className="stui-pill-group">
          {options.map(o => (
            <button key={o.value} className={`stui-pill${value === o.value ? ' selected' : ''}`}
              disabled={disabled} onClick={() => stage(def, o.value)}>{o.label}</button>
          ))}
        </div>
      );
    } else {
      control = (
        <input type="text" className="stui-select" value={value || ''} disabled={disabled}
          onChange={e => stage(def, e.target.value)} />
      );
    }

    return (
      <div key={def.prefKey} className="stui-row-setting">
        <div className="stui-row-setting-label">
          <div className="stui-label">
            {def.label}
            {isFuture && <span className="stui-badge-construction">Under Construction</span>}
          </div>
          <div className="stui-sublabel">
            {overridden
              ? `Overridden at ${scopeLabel} (catalog default: ${def.defaultValue || '—'})`
              : `Not set at this scope — catalog default: ${def.defaultValue || '—'}`}
          </div>
          {err && <div className="stui-sublabel" style={{ color: 'var(--color-error)' }}>{err}</div>}
        </div>
        <div className="stui-row-setting-controls">
          {control}
          {overridden && isEditing && !isFuture && (
            <button className="stui-btn stui-btn-secondary" title="Remove this override — value falls back down the chain"
              onClick={() => stage(def, null)}>
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>
    );
  };

  const sections = useMemo(() => {
    const visible = catalog
      .filter(d => d.status !== 'hidden')
      .sort((a, b) =>
        TAB_ORDER.indexOf(a.tab) - TAB_ORDER.indexOf(b.tab) ||
        a.sortOrder - b.sortOrder);
    const out = [];
    visible.forEach(d => {
      const title = d.section ? `${d.tab} — ${d.section}` : d.tab;
      let group = out.find(g => g.title === title);
      if (!group) { group = { title, defs: [] }; out.push(group); }
      group.defs.push(d);
    });
    return out;
  }, [catalog]);

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <SlidersHorizontal size={18} />
          <h1>Preference Defaults</h1>
          <span className="stui-module-header-meta">
            Defaults by system, database, and role — user selections always win
          </span>
        </div>
        <div className="stui-module-header-actions">
          <button className="stui-crud-btn" title={isEditing ? 'Cancel editing' : 'Edit defaults'}
            onClick={isEditing ? handleCancel : () => setIsEditing(true)} disabled={loading || loadError}>
            {isEditing ? <X size={16} /> : <Pencil size={16} />}
          </button>
          <button className={`stui-crud-btn ${!(isEditing && isDirty) || saving ? 'disabled' : ''}`}
            title="Save defaults" onClick={handleSave} disabled={!(isEditing && isDirty) || saving}>
            <Save size={16} />
          </button>
        </div>
      </div>

      <div className="stui-tab-body">
        {loadError && (
          <div className="stui-label-note" style={{ color: 'var(--color-error)' }}>
            Failed to load preference defaults — SysAdmin access required.
          </div>
        )}
        {saveErrors._global && (
          <div className="stui-label-note" style={{ color: 'var(--color-error)' }}>{saveErrors._global}</div>
        )}
        {loading && <div className="stui-label-note">Loading…</div>}

        {!loading && !loadError && (
          <>
            <SectionHeader title="Scope" />
            <div className="stui-row-setting">
              <div className="stui-row-setting-label">
                <div className="stui-label">Editing Defaults For</div>
                <div className="stui-sublabel">
                  Pick the database and role this page edits. More specific scopes beat broader ones;
                  a user's own preference always beats every default.
                </div>
              </div>
              <div className="stui-row-setting-controls">
                <select className="stui-select" value={scopeDbId == null ? 'system' : String(scopeDbId)}
                  disabled={!isEditing}
                  onChange={e => setScopeDbId(e.target.value === 'system' ? null : Number(e.target.value))}>
                  <option value="system">System-wide (all databases)</option>
                  {databases.map(d => <option key={d.id} value={String(d.id)}>{d.displayName || d.slug}</option>)}
                </select>
                <select className="stui-select" value={scopeRole == null ? 'all' : scopeRole}
                  disabled={!isEditing}
                  onChange={e => setScopeRole(e.target.value === 'all' ? null : e.target.value)}>
                  <option value="all">All Roles</option>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            {sections.map(group => (
              <React.Fragment key={group.title}>
                <SectionHeader title={group.title} />
                {group.defs.map(renderControl)}
              </React.Fragment>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default PreferenceDefaultsPage;
